import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addDays,
  format,
} from "date-fns";
import { Download, LayoutGrid, List, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SubNavigation } from "@/components/pages/reservations/components/SubNavigation";
import { StaffFilterButton } from "./components/StaffFilterButton";
import { DateRangePicker } from "@/components/common/DateRangePicker";
import { DateRange } from "@/hooks/useDateRange";
import { useCurrentHotelId } from "@/hooks/useCurrentHotelId";
import {
  useStaffAvailabilityRange,
  useStaffSchedules,
} from "@/hooks/useStaffSchedule";
import { useStaff } from "@/components/pages/staff/hooks/useStaff";
import { useTeams } from "@/components/pages/staff/hooks/useTeams";
import { DayOffRequest, StaffSchedule } from "@/types/staff-schedule";
import { ScheduleGrid } from "./components/ScheduleGrid";
import { ScheduleTable } from "./components/ScheduleTable";
import { DayOffRequestsTable } from "./components/DayOffRequestsTable";
import { EditScheduleDialog } from "./components/EditScheduleDialog";
import { CreateScheduleDialog } from "./components/CreateScheduleDialog";
import { ReviewDayOffDialog } from "./components/ReviewDayOffDialog";
import { useCSVExport } from "@/hooks/useCSVExport";
import {
  formatEffectivePeriod,
  formatHours,
  formatPattern,
} from "./utils/formatSchedule";

type DisplayMode = "grid" | "table";
type ViewMode = "week" | "2weeks" | "month";
type PageTab = "schedule" | "requests";

export const ScheduleManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const hotelId = useCurrentHotelId();
  const { exportToCSV } = useCSVExport();

  const [activeTab, setActiveTab] = useState<PageTab>("schedule");
  const [editingSchedule, setEditingSchedule] = useState<StaffSchedule | null>(
    null,
  );
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState<DayOffRequest | null>(null);

  const [displayMode, setDisplayMode] = useState<DisplayMode>("grid");
  const [viewMode, setViewMode] = useState<ViewMode>("week");
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const today = new Date();
    return {
      from: startOfWeek(today, { weekStartsOn: 1 }),
      to: endOfWeek(today, { weekStartsOn: 1 }),
    };
  });

  const { data: schedules } = useStaffSchedules(hotelId ?? "");
  const { data: staffData } = useStaff({ hotelId: hotelId ?? undefined });
  const { data: teamsData } = useTeams();
  const teams = useMemo(() => teamsData?.data ?? [], [teamsData]);

  const navTabs = useMemo(
    () => [
      { id: "schedule", label: t("staffSchedule.nav.schedule") },
      { id: "requests", label: t("staffSchedule.nav.requests") },
    ],
    [t],
  );

  const staffNames = useMemo(() => {
    const map: Record<string, string> = {};
    if (staffData?.users) {
      for (const user of staffData.users) {
        const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
        map[user.id] = name || user.email || "—";
        if (user.staff?.id) {
          map[user.staff.id] = name || user.email || "—";
        }
      }
    }
    return map;
  }, [staffData]);

  const staffOptions = useMemo(() => {
    if (!staffData?.users) return [];
    return staffData.users
      .filter((u) => u.staff?.isActive !== false)
      .map((user) => ({
        id: user.staff?.id ?? user.id,
        name:
          [user.firstName, user.lastName].filter(Boolean).join(" ") ||
          user.email ||
          "—",
      }));
  }, [staffData]);

  const staffFilterOptions = useMemo(
    () => staffOptions.map((s) => ({ value: s.id, label: s.name })),
    [staffOptions],
  );

  const filteredSchedules = useMemo(() => {
    if (!schedules) return [];
    if (selectedStaffIds.length === 0) return schedules;
    return schedules.filter((s) => selectedStaffIds.includes(s.staffId));
  }, [schedules, selectedStaffIds]);

  const handleViewModeChange = (mode: string) => {
    const newMode = mode as ViewMode;
    setViewMode(newMode);
    const today = new Date();
    const ws = startOfWeek(today, { weekStartsOn: 1 });
    if (newMode === "week") {
      setDateRange({ from: ws, to: endOfWeek(today, { weekStartsOn: 1 }) });
    } else if (newMode === "2weeks") {
      setDateRange({ from: ws, to: addDays(ws, 13) });
    } else {
      setDateRange({ from: startOfMonth(today), to: endOfMonth(today) });
    }
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    if (range.from && range.to) {
      const days = Math.round(
        (range.to.getTime() - range.from.getTime()) / (1000 * 60 * 60 * 24),
      );
      if (days <= 7) setViewMode("week");
      else if (days <= 14) setViewMode("2weeks");
      else setViewMode("month");
    }
  };

  const ganttStartDate = dateRange.from
    ? format(dateRange.from, "yyyy-MM-dd")
    : "";
  const ganttEndDate = dateRange.to ? format(dateRange.to, "yyyy-MM-dd") : "";
  const { data: availabilityRangeData } = useStaffAvailabilityRange(
    hotelId ?? "",
    ganttStartDate,
    ganttEndDate,
  );

  const filteredAvailabilityRangeData = useMemo(() => {
    if (!availabilityRangeData) {
      return [];
    }

    let result = availabilityRangeData;

    if (selectedStaffIds.length > 0) {
      result = result.filter((staff) =>
        selectedStaffIds.includes(staff.staffId),
      );
    }

    if (selectedTeamId) {
      const selectedTeam = teams.find((team) => team.id === selectedTeamId);
      if (selectedTeam) {
        result = result.filter((staff) => staff.teamName === selectedTeam.name);
      }
    }

    return result;
  }, [availabilityRangeData, selectedStaffIds, selectedTeamId, teams]);

  const handleExport = () => {
    if (displayMode === "table") {
      if (filteredSchedules.length === 0) {
        return;
      }

      const tableRows = filteredSchedules.map((schedule) => ({
        staff: staffNames[schedule.staff?.profileId] ?? "—",
        pattern: formatPattern(schedule, t),
        hours: formatHours(schedule),
        effective: formatEffectivePeriod(schedule, t),
        active: schedule.isActive ? "Yes" : "No",
      }));

      exportToCSV(tableRows, "staff-schedule-table-export.csv");
      return;
    }

    const gridRows = filteredAvailabilityRangeData.flatMap((staff) =>
      staff.days.map((day) => ({
        staff:
          [staff.firstName, staff.lastName].filter(Boolean).join(" ") ||
          staffNames[staff.staffId] ||
          staffNames[staff.profileId ?? ""] ||
          "—",
        team: staff.teamName ?? "—",
        position: staff.position ?? "—",
        date: day.date,
        availability: day.isAvailable
          ? t("staffSchedule.schedule.onShift")
          : t("staffSchedule.schedule.dayOff"),
        startTime: day.startTime ?? "—",
        endTime: day.endTime ?? "—",
        tasksCount: day.tasksCount,
        hasDayOff: day.hasDayOff ? "Yes" : "No",
        hasDayOffRequest: day.hasDayOffRequest ? "Yes" : "No",
      })),
    );

    if (gridRows.length === 0) {
      return;
    }

    exportToCSV(gridRows, "staff-schedule-export.csv");
  };

  const canExport =
    displayMode === "table"
      ? filteredSchedules.length > 0
      : filteredAvailabilityRangeData.length > 0;

  if (!hotelId) return null;

  return (
    <div className="flex flex-col gap-4 p-4 md:p-8 max-w-[1344px] mx-auto">
      {/* Top navigation tabs */}
      <SubNavigation
        activeTab={activeTab}
        onTabChange={(tab) => setActiveTab(tab as PageTab)}
        tabs={navTabs}
      />

      {/* Schedule tab */}
      {activeTab === "schedule" && (
        <>
          {/* Toolbar */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <StaffFilterButton
                options={staffFilterOptions}
                value={selectedStaffIds}
                onValueChange={setSelectedStaffIds}
              />
              <DateRangePicker
                dateRange={dateRange}
                onDateRangeChange={handleDateRangeChange}
                className="flex items-center gap-2 min-h-8 h-8 py-1 border border-slate-200 text-foreground text-sm font-medium shadow-xs"
              />
              {displayMode === "grid" && (
                <Select value={viewMode} onValueChange={handleViewModeChange}>
                  <SelectTrigger
                    size="sm"
                    className="w-[110px] border-slate-200 shadow-xs"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="week">
                      {t("staffSchedule.schedule.viewWeek")}
                    </SelectItem>
                    <SelectItem value="2weeks">
                      {t("staffSchedule.schedule.view2Weeks")}
                    </SelectItem>
                    <SelectItem value="month">
                      {t("staffSchedule.schedule.viewMonth")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="flex items-center gap-2">
              <div className="flex rounded-md border border-slate-200 shadow-xs">
                <Button
                  variant={displayMode === "grid" ? "default" : "ghost"}
                  size="icon_sm"
                  className="rounded-none rounded-l-md"
                  onClick={() => setDisplayMode("grid")}
                  aria-label={t("staffSchedule.schedule.gridView")}
                >
                  <LayoutGrid className="size-4" />
                </Button>
                <Button
                  variant={displayMode === "table" ? "default" : "ghost"}
                  size="icon_sm"
                  className="rounded-none rounded-r-md border-l border-slate-200"
                  onClick={() => setDisplayMode("table")}
                  aria-label={t("staffSchedule.schedule.tableView")}
                >
                  <List className="size-4" />
                </Button>
              </div>

              <Button
                variant="outline"
                size="default"
                className="gap-2"
                onClick={handleExport}
                disabled={!canExport}
              >
                <Download className="size-4" />
                <span className="hidden sm:inline">
                  {t("staffSchedule.page.export")}
                </span>
              </Button>

              <Button
                variant="new_default"
                size="default"
                className="gap-2"
                onClick={() => setCreateDialogOpen(true)}
              >
                <Plus className="size-4" />
                <span className="hidden sm:inline">
                  {t("staffSchedule.page.createSchedule")}
                </span>
              </Button>
            </div>
          </div>

          {/* Schedule content */}
          {displayMode === "grid" && (
            <ScheduleGrid
              hotelId={hotelId}
              selectedStaffIds={selectedStaffIds}
              selectedTeamId={selectedTeamId}
              startDate={ganttStartDate}
              endDate={ganttEndDate}
              schedules={schedules ?? []}
              teams={teams}
              staffNames={staffNames}
              onScheduleClick={setEditingSchedule}
              onDayOffRequestClick={setReviewTarget}
              onTeamChange={setSelectedTeamId}
            />
          )}
          {displayMode === "table" && (
            <ScheduleTable
              data={filteredSchedules}
              isLoading={!schedules}
              hotelId={hotelId}
              onEdit={setEditingSchedule}
              staffNames={staffNames}
            />
          )}
        </>
      )}

      {/* Requests tab */}
      {activeTab === "requests" && (
        <DayOffRequestsTable hotelId={hotelId} staffNames={staffNames} />
      )}

      {/* Dialogs */}
      <CreateScheduleDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        hotelId={hotelId}
        staffOptions={staffOptions}
      />
      <EditScheduleDialog
        schedule={editingSchedule}
        hotelId={hotelId}
        staffOptions={staffOptions}
        onClose={() => setEditingSchedule(null)}
      />
      <ReviewDayOffDialog
        request={reviewTarget}
        staffName={
          reviewTarget ? (staffNames[reviewTarget.staffId] ?? "—") : ""
        }
        onClose={() => setReviewTarget(null)}
      />
    </div>
  );
};
